'use client';

import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { findFirstFocusableIndex, getNextFocusableIndex } from './rovingFocusUtils';
type UseRovingChoiceFocusOptions = {
  itemCount: number;
  isItemFocusable?: (index: number) => boolean;
  /** When this value changes, focus resets to the first focusable item. */
  resetKey?: string | number;
  /** For radio groups: arrow keys move selection as well as focus. */
  onArrowSelect?: (index: number) => void;
  /** Index that receives the single tab stop on first render. */
  initialIndex?: number;
};
export function useRovingChoiceFocus({
  itemCount,
  isItemFocusable = () => true,
  resetKey,
  onArrowSelect,
  initialIndex
}: UseRovingChoiceFocusOptions) {
  const isItemFocusableRef = useRef(isItemFocusable);
  isItemFocusableRef.current = isItemFocusable;
  const onArrowSelectRef = useRef(onArrowSelect);
  onArrowSelectRef.current = onArrowSelect;
  const checkFocusable = (index: number) => isItemFocusableRef.current(index);
  const [focusedIndex, setFocusedIndex] = useState(() => {
    if (initialIndex !== undefined && initialIndex >= 0 && initialIndex < itemCount && checkFocusable(initialIndex)) {
      return initialIndex;
    }
    return findFirstFocusableIndex(itemCount, checkFocusable);
  });
  const itemRefs = useRef<(HTMLInputElement | null)[]>([]);
  const skipAutoFocusRef = useRef(true);
  useEffect(() => {
    setFocusedIndex(findFirstFocusableIndex(itemCount, checkFocusable));
  }, [resetKey, itemCount]);
  useEffect(() => {
    if (focusedIndex >= itemCount) {
      setFocusedIndex(findFirstFocusableIndex(itemCount, checkFocusable));
    }
  }, [focusedIndex, itemCount]);
  useEffect(() => {
    if (skipAutoFocusRef.current) {
      skipAutoFocusRef.current = false;
      return;
    }
    // Only steal DOM focus if it's already within this list (e.g. arrow-key
    // navigation or a filter reset while an item was focused). Otherwise focus
    // may be elsewhere (e.g. the search input), and this would yank it away.
    const isFocusWithinList = itemRefs.current.some(el => el === document.activeElement);
    if (isFocusWithinList) {
      itemRefs.current[focusedIndex]?.focus();
    }
  }, [focusedIndex]);
  const getTabIndex = (index_0: number) => {
    if (itemCount === 0) return -1;
    return index_0 === focusedIndex ? 0 : -1;
  };
  const setItemRef = (index_1: number) => (element: HTMLInputElement | null) => {
    itemRefs.current[index_1] = element;
  };
  const handleFocus = (index_2: number) => () => {
    setFocusedIndex(index_2);
  };
  const handleKeyDown = (index_3: number) => (event: KeyboardEvent<HTMLInputElement>) => {
    const next = getNextFocusableIndex(index_3, event.key, itemCount, checkFocusable);
    if (next === null) return;
    event.preventDefault();
    setFocusedIndex(next);
    onArrowSelectRef.current?.(next);
  };
  return {
    getTabIndex,
    setItemRef,
    handleFocus,
    handleKeyDown
  };
}